import MVT from 'ol/format/MVT';
import BaseLayer from 'ol/layer/Base';
import {Component, computed, effect, inject, Signal} from '@angular/core';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatOption} from '@angular/material/core';
import {MatFormField, MatLabel} from '@angular/material/input';
import {MatSelect} from '@angular/material/select';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {debounceTime, filter, merge} from 'rxjs';
import {AppService} from '../../../../services/app.service';
import {MapService} from '../../../../services/map.service';
import {BaseUILayer} from '../../../classes/layers/base-ui-layer';
import {LayerHeaderComponent} from '../../layer-header/layer-header.component';

@Component({
  selector: 'tgrid-affordability-layer',
  standalone: true,
  imports: [
    LayerHeaderComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
  ],
  templateUrl: './affordability-layer.component.html',
  styleUrl: './affordability-layer.component.scss',
})
export class AffordabilityLayerComponent extends BaseUILayer<BaseLayer> {
  private appService = inject(AppService);

  private sourceUrl: Signal<string> = computed(() => {
    const abbreviations = this.appService.selectedStates().map(s => s.abbreviation).join(',');
    const url = this.mapLayer().serviceUrl;

    return abbreviations.length > 0 ? `${url}?state=${abbreviations}` : url;
  });

  protected metrics = [
    {key: 'electricityAffordabilityIndex', label: 'Electricity Affordability'},
    {key: 'gasAffordabilityIndex', label: 'Gas Affordability'},
    {key: 'waterAffordabilityIndex', label: 'Water Affordability'},
    {key: 'rentAffordabilityIndex', label: 'Rent Affordability'},
  ];

  protected legendElements = [
    {label: 'Very High Affordability', color: '#0b7a40'},
    {label: 'High Affordability', color: '#8ccf7a'},
    {label: 'Moderate Affordability', color: '#f3de72'},
    {label: 'Low Affordability', color: '#f19a3e'},
    {label: 'Very Low Affordability', color: '#c83a3a'},
  ];

  protected metricBreakpoints: Record<string, number[]> = {
    electricityAffordabilityIndex: [-0.1663475054057335, -0.026444617039266705, 0.07697997036026272, 0.19103381464016822],
    gasAffordabilityIndex: [-0.24488442346738992, -0.037418567437002226, 0.12765659767945803, 0.3061588874685519],
    waterAffordabilityIndex: [-0.17134890381848833, -0.015170237635009576, 0.08469664075530203, 0.2017005456086033],
    rentAffordabilityIndex: [-0.10524585193000657, -0.007282301054620577, 0.06689208519907075, 0.14086552227516558 ],
  };

  protected filterGroup: FormGroup = new FormGroup({
    metric: new FormControl<string>('electricityAffordabilityIndex', {nonNullable: true}),
  });

  protected colors = computed(() => {
    const selectedMetric = this.filterGroup.get('metric')?.value || 'electricityAffordabilityIndex';
    return this.metricBreakpoints[selectedMetric] || this.metricBreakpoints['electricityAffordabilityIndex'];
  });

  constructor() {
    super();

    effect(() => {
      const newUrl = this.sourceUrl();
      if (this.checked) {
        const source = (this.mapLayer().renderedLayer as VectorTileLayer).getSource();
        source?.setUrl(newUrl);
      }
    });

    merge(this.filterGroup.valueChanges, toObservable(this.appService.selectedStates))
      .pipe(
        filter(() => this.checked),
        debounceTime(250),
        takeUntilDestroyed(),
      ).subscribe(() => {
      if (this.mapLayer().renderedLayer) {
        (this.mapLayer().renderedLayer as VectorTileLayer).changed();
      }
    });
  }

  private getAffordabilityColor(properties: Record<string, unknown>): string {
    const selectedMetric = this.filterGroup.get('metric')?.value || 'electricityAffordabilityIndex';
    const value = Number(properties[selectedMetric]);

    if (!Number.isFinite(value)) {
      return '#444444';
    }

    const breakpoints = this.metricBreakpoints[selectedMetric] || this.metricBreakpoints['electricityAffordabilityIndex'];

    if (value < breakpoints[0]) return '#c83a3a';
    else if (value >= breakpoints[0] && value < breakpoints[1]) return '#f19a3e';
    else if (value >= breakpoints[1] && value < breakpoints[2]) return '#f3de72';
    else if (value >= breakpoints[2] && value < breakpoints[3]) return '#8ccf7a';
    else if (value >= breakpoints[3]) return '#0b7a40';
    else return '#444444';
  }

  override toggleLayer(checked: boolean):
    void {
    this.checked = checked;
    if (this.checked
    ) {
      if (this.mapLayer().renderedLayer) {
        const source = (this.mapLayer().renderedLayer as VectorTileLayer).getSource();
        source?.setUrl(this.sourceUrl());
      } else {
        this.mapLayer().renderedLayer = new VectorTileLayer({
          properties: {
            type: this.mapLayer().serviceType,
            name: this.mapLayer().displayName,
            popoverFunction: this.mapLayer().createPopover.bind(this.mapLayer()),
          },
          source: new VectorTileSource({
            format: new MVT(),
            url: this.sourceUrl(),
            attributions: this.mapLayer().buildAttribution(),
          }),
          style: (feature) => {
            const properties = feature.getProperties();

            return new Style({
              fill: new Fill({
                color: this.getAffordabilityColor(properties),
              }),
              stroke: new Stroke({
                color: '#111111',
                width: 1,
              }),
            });
          },
        });
      }

      MapService.getMapRef()!.addLayer(this.mapLayer().renderedLayer);
      this.changeOpacity(this.opacity);

    } else {
      MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
    }
  }

  override changeOpacity(value: number) {
    this.opacity = value;
    (this.mapLayer().renderedLayer as VectorTileLayer).setOpacity(value / 100);
  }

  override onDestroy(): void {
  }
}


